import { APIGet, APIPost, APIPut, APIDelete, APIUrl, AdminType } from "api/protocols";
import { CancelToken } from "axios";

export interface IPostLineItemToCart {
  cart_id: number;
  parent_id?: number;
  note?: string;
  kitchen_meal_id?: number;

  // Add single fee at a time
  variant_id?: number;
  quantity?: number;

  // Add multiple fees at a time.
  greenFeeCounts?: MultipleFeeCounts[];
  powerCartCounts?: MultipleFeeCounts[];
}
type MultipleFeeCounts = {
  variant_id: string;
  quantity: number;
};

export interface IPutLineItemToCart {
  id: number;
  quantity?: number;
  price?: number;
  note?: string;
  kitchen_meal_id?: number;
}

export interface IPutPrice {
  id: number;
  price: number;
}

export interface IDeleteLineItemToCart {
  id: number;
}

export interface IGetCart {
  id?: number;
  token?: string;
  tableCart?: Record<string, any>;
  is_tab?: boolean;
}

interface ISplitLineItem {
  line_item_ids: Array<number>;
  cart_ids: Array<number>;
}

export interface IPostCart {
  type?: string;
  register_id?: number;
  table_id?: number;
  customer_id?: number;
}

export interface ICartAudit {
  id: number;
  cart_id: number;
  user_id: number;
  title: string;
  description: string;
  action: "create" | "update" | "delete";
  previous_value: Record<string, any>;
  new_value: Record<string, any>;
  created_at: string;
}

// GET Cart
// Retrieve a list of carts
export async function GetCart(params: IGetCart, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/cart"), params, useGlobalLoader);
}

export async function GetCartAudits(params: { id: number }, useGlobalLoader: boolean, cancelToken?: CancelToken) {
  return await APIGet<Array<ICartAudit>>(
    APIUrl(AdminType.FacilityAdmin, "/cart/audit"),
    params,
    useGlobalLoader,
    cancelToken,
  );
}

// POST Cart
// Create a new, empty cart
export async function PostCart(params: IPostCart, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/cart"), params, useGlobalLoader);
}

// //PUT Cart
export async function PutCart(params: any, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/cart"), params, useGlobalLoader);
}

// VOID Cart
export async function PutVoid(params: any, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/cart/void"), params, useGlobalLoader);
}

export async function PutPrice(params: IPutPrice, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/cart/line-item/price"), params, useGlobalLoader);
}

export interface IPutClearCart {
  cart_id: number;
}

export async function PutClearCart(params: IPutClearCart, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/cart/clear"), params, useGlobalLoader);
}

export async function PostLineItemToCart(params: IPostLineItemToCart, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/cart/line-item"), params, useGlobalLoader);
}
export async function PostLineItemToCartTemp(params: IPostLineItemToCart, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/cart/line-item/bulk"), params, useGlobalLoader);
}

export async function PutLineItemToCart(params: IPutLineItemToCart, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/cart/line-item"), params, useGlobalLoader);
}

export async function DeleteLineItemToCart(params: IDeleteLineItemToCart, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/cart/line-item"), params, useGlobalLoader);
}

export async function PutLineItemSplit(params: ISplitLineItem, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/cart/line-item/split"), params, useGlobalLoader);
}

export interface IMoveLineItem {
  id: number;
  cart_id: number;
  previous_cart_id: number;
  quantity: number;
  child_line_item: boolean;
}

export interface IPutMoveLineItems {
  line_items: IMoveLineItem[];
}

export async function PutMoveLineItems(params: IPutMoveLineItems, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/cart/line-item/move"), params, useGlobalLoader);
}

// POST Merge Cart
// Merge 2 or more carts
interface IPostMerge {
  cart_ids: number[];
  table_id?: number;
}

export async function PostMerge(params: IPostMerge, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/cart/merge"), params, useGlobalLoader);
}

// POST Cart Checkout
// Migrate a cart to an order to process payments
interface IPostCheckout {
  cart_id: number;
  register_id: number;
}

export async function PostCheckout(params: IPostCheckout, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/cart/checkout"), params, useGlobalLoader);
}

//REGISTER TABS
interface ISaveCartAsTab {
  cart_id: number;
  customer_id?: number | null;
  description?: string;
  customer_payment_method_id?: number | null;
}

export async function SaveCartAsTab(params: ISaveCartAsTab, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/cart/tab"), params, useGlobalLoader);
}

interface ICloseTab {
  cart_id: number;
  cancel_reason?: string;
}
export async function CloseTab(params: ICloseTab, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/cart/tab/close"), params, useGlobalLoader);
}

//Service Fees

interface IPostServiceFeeLineItem {
  service_fee_id: number;
  facility_id: number;
  cart_id: number;
}

export async function PostServiceFeeLineItem(params: IPostServiceFeeLineItem, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/cart/service-fee"), params, useGlobalLoader);
}

interface IDeleteServiceFeeLineItem {
  // service_fee_id: number;
  facility_id: number;
  cart_id: number;
}

export async function DeleteServiceFeeLineItem(params: IDeleteServiceFeeLineItem, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/cart/service-fee"), params, useGlobalLoader);
}

export async function RemoveCardFromTab(params: { id: number }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/cart/tab/remove"), params, useGlobalLoader);
}
